import React, { useEffect, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import SystemListScreen from "./systemList";
import SystemDetailsScreen from "./systemDetails";
import { Box } from "@mui/system";
import SystemTabTitle from "../../components/SystemTabTitle";
import { ListRounded } from "@mui-symbols-material/w300";
import { useTranslation } from "react-i18next";
import AutoScrollTabs from "../../components/AutoScrollTabs";
import { useKeycloak } from "@react-keycloak/web";

const LEGACY_STORAGE_KEY = "openSystemTabs";

const SystemRoutes = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { keycloak } = useKeycloak();

    const userId = keycloak.tokenParsed?.sub;
    const storageKey = userId ? `openSystemTabs_${userId}` : null;

    // Extract systemId from URL
    const match = location.pathname.match(/^\/systems\/([^/]+)/);
    const activeTab = match?.[1] ?? "list";

    const [openTabs, setOpenTabs] = useState<string[]>([]);
    const [tabsLoaded, setTabsLoaded] = useState(false);

    // Load tabs from localStorage on mount
    useEffect(() => {
        // Remove legacy non-user-scoped key
        localStorage.removeItem(LEGACY_STORAGE_KEY);

        if (storageKey) {
            const stored = localStorage.getItem(storageKey);
            if (stored) {
                try {
                    setOpenTabs(JSON.parse(stored));
                } catch (e) {
                    console.warn("Could not parse localStorage tabs");
                }
            }
        }
        setTabsLoaded(true);
    }, []);

    // Save tabs to localStorage on change
    useEffect(() => {
        if (tabsLoaded && storageKey) {
            localStorage.setItem(storageKey, JSON.stringify(openTabs));
        }
    }, [openTabs, tabsLoaded]);

    // Add new tab when navigating to a system detail
    useEffect(() => {
        if (!tabsLoaded) return;

        if (activeTab !== "list" && !openTabs.includes(activeTab)) {
            setOpenTabs((prev) => [...prev, activeTab]);
        }
    }, [activeTab, tabsLoaded]);

    const handleTabChange = (event: React.SyntheticEvent | null, value: number | string | null) => {
        if (value === "list") {
            navigate("/systems");
        } else {
            navigate(`/systems/${value}/details`);
        }
    };

    const handleTabClose = (systemId: string) => {
        const updatedTabs = openTabs.filter((id) => id !== systemId);
        setOpenTabs(updatedTabs);

        if (activeTab === systemId) {
            const fallback = updatedTabs.length > 0 ? updatedTabs[updatedTabs.length - 1] : "list";
            navigate(fallback === "list" ? "/systems" : `/systems/${fallback}/details`);
        }
    };

    return (
        <Box sx={{ padding: "8px 12px", maxWidth: "100%" }} flexDirection="column" display={"flex"} flexGrow={1}>
            <AutoScrollTabs
                value={activeTab}
                onChange={handleTabChange}
                tabs={[
                    { label: t("List"), value: "list", icon: <ListRounded />, sticky: true },
                    ...openTabs.map((id) => ({
                        label: <SystemTabTitle systemId={id} onCloseTab={handleTabClose} />,
                        value: id,
                    })),
                ]}
                onCloseAll={() => {
                    setOpenTabs([]);
                    navigate("/systems");
                }}
                onCloseOthers={() => {
                    const filteredTabs = openTabs.filter((id) => id === activeTab);
                    setOpenTabs(filteredTabs);
                }}
            />

            <Routes>
                <Route path="/" element={<SystemListScreen onOpenSystem={(id) => navigate(`/systems/${id}/details`)} />} />
                <Route path="/:systemId/*" element={<SystemDetailsScreen onSystemDeleted={handleTabClose} />} />
            </Routes>
        </Box>
    );
};

export default SystemRoutes;
